<template>
  <AppDropdown
    :model-value="open"
    :anchor="triggerSelector"
    placement="bottom-end"
    :close-on-content-click="false"
    :close-on-outside-click="false"
    content-class="z-builds-popup!"
    @update:model-value="onDropdownUpdate"
  >
    <section
      ref="dialogElement"
      :id="popupId"
      class="w-(--max-w-popup-builds) max-w-(--max-w-popup-builds) max-h-(--max-h-popup-builds) overflow-y-auto overscroll-contain bg-bg-elevated border border-border-strong rounded-md shadow-lg p-1.5 flex flex-col gap-1"
      role="dialog"
      :aria-labelledby="headingId"
      tabindex="-1"
      @keydown="onDialogKeydown"
    >
      <header class="flex items-start justify-between gap-2 px-1.5 pt-1 pb-1.5 border-b border-border" role="presentation">
        <span class="min-w-0">
          <span class="block mt-0.5 text-xs leading-tight text-text-muted">{{ messages.builds.chooserHint }}</span>
          <span :id="headingId" class="block font-display text-xs font-semibold tracking-wide uppercase text-text-primary">{{ messages.builds.savedBuilds }}</span>
        </span>
        <span class="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-sm bg-bg-surface text-2xs font-mono text-text-muted">
          {{ totalBuildCount }}
        </span>
      </header>
      <h3 class="px-2 pt-1 text-xs font-semibold tracking-wide uppercase text-text-muted">{{ messages.builds.playerBuilds }}</h3>
      <div v-if="props.player.builds.length > 0" class="flex flex-col gap-1" role="group" :aria-label="messages.builds.savedBuilds">
        <template v-for="(_, idx) in props.player.builds" :key="`b-${idx}`">
          <button
            v-if="props.canSelectBuilds && isSelectableBuild(idx)"
            type="button"
            data-build-action="select"
            :data-active-build="isActiveBuild(idx) ? 'true' : undefined"
            :class="[
              'group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 w-full px-2 py-1.5 rounded-sm border text-left transition-[border-color,background-color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
              showActiveIndicator(idx)
                ? 'border-accent bg-accent/10'
                : 'border-transparent hover:border-border hover:bg-bg-surface',
            ]"
            :title="rowTitle(idx)"
            @click="onUseBuild(idx)"
          >
            <span class="inline-flex items-center gap-0.5 p-1 rounded-sm bg-bg-surface border border-border shrink-0">
              <template v-for="(slug, slotIdx) in buildSlotLabels(idx)" :key="`b-${idx}-${slotIdx}`">
                <img
                  v-if="weaponIcon(slug)"
                  :src="weaponIcon(slug)"
                  :alt="weaponName(slug) ?? ''"
                  class="w-6 h-6 object-cover rounded-sm shrink-0"
                  draggable="false"
                />
                <span
                  v-else
                  class="w-6 h-6 inline-flex items-center justify-center text-xs font-bold text-text-muted border border-dashed border-border-strong rounded-sm shrink-0"
                  aria-hidden="true"
                >+</span>
              </template>
            </span>
            <span class="min-w-0">
              <span class="flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-xs font-semibold text-text-primary">
                <span>{{ messages.builds.buildLabel(idx + 1) }}</span>
                <span v-if="idx === 0" class="rounded-sm border border-accent/40 bg-accent/10 px-1 py-0.5 text-2xs uppercase tracking-wide text-accent">{{ messages.builds.defaultLabel }}</span>
                <BuildStageBadges :stage-names="stageNamesForBuild(props.player.builds[idx] ?? [])" />
              </span>
              <span class="block mt-0.5 text-xs leading-tight text-text-muted truncate">{{ buildSummary(idx) }}</span>
            </span>
            <span
              v-if="showActiveIndicator(idx)"
              class="inline-flex items-center gap-1 text-xs font-semibold tracking-[0.04em] uppercase text-accent shrink-0"
            >
              <span aria-hidden="true">✓</span>
              <span>{{ messages.builds.activeLabel }}</span>
            </span>
            <span v-else class="text-sm text-text-muted opacity-0 group-hover:opacity-100" aria-hidden="true">→</span>
          </button>
          <div
            v-else
            :class="[
              'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 w-full px-2 py-1.5 rounded-sm border text-left',
              showActiveIndicator(idx) ? 'border-accent bg-accent/10' : 'border-transparent',
            ]"
            :title="readOnlyRowTitle(idx)"
          >
            <span class="inline-flex items-center gap-0.5 p-1 rounded-sm bg-bg-surface border border-border shrink-0">
              <template v-for="(slug, slotIdx) in buildSlotLabels(idx)" :key="`r-${idx}-${slotIdx}`">
                <img
                  v-if="weaponIcon(slug)"
                  :src="weaponIcon(slug)"
                  :alt="weaponName(slug) ?? ''"
                  class="w-6 h-6 object-cover rounded-sm shrink-0"
                  draggable="false"
                />
                <span
                  v-else
                  class="w-6 h-6 inline-flex items-center justify-center text-xs font-bold text-text-muted border border-dashed border-border-strong rounded-sm shrink-0"
                  aria-hidden="true"
                >+</span>
              </template>
            </span>
            <span class="min-w-0">
              <span class="flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-xs font-semibold text-text-primary">
                <span>{{ messages.builds.buildLabel(idx + 1) }}</span>
                <span v-if="idx === 0" class="rounded-sm border border-accent/40 bg-accent/10 px-1 py-0.5 text-2xs uppercase tracking-wide text-accent">{{ messages.builds.defaultLabel }}</span>
                <BuildStageBadges :stage-names="stageNamesForBuild(props.player.builds[idx] ?? [])" />
              </span>
              <span class="block mt-0.5 text-xs leading-tight text-text-muted truncate">{{ buildSummary(idx) }}</span>
            </span>
            <span
              v-if="showActiveIndicator(idx)"
              class="inline-flex items-center gap-1 text-xs font-semibold tracking-[0.04em] uppercase text-accent shrink-0"
            >
              <span aria-hidden="true">✓</span>
              <span>{{ messages.builds.activeLabel }}</span>
            </span>
            <span
              v-else-if="!isSelectableBuild(idx)"
              class="text-xs font-semibold text-text-muted shrink-0"
            >{{ messages.builds.requiresDistinctWeapons }}</span>
          </div>
          <div v-if="props.canSetDefaultBuild && idx > 0" class="flex items-center justify-end px-1">
            <button
              type="button"
              data-build-action="set-default"
              class="px-1.5 py-0.5 text-xs text-text-secondary hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
              @click="onSetDefaultBuild(idx)"
            >{{ messages.builds.makeDefault }}</button>
          </div>
        </template>
      </div>
      <p v-else class="px-2 py-3 text-xs leading-relaxed text-text-muted">{{ messages.builds.noSavedBuilds }}</p>
      <button
        v-if="canQuickCreateBuild"
        type="button"
        data-build-action="quick-create-personal"
        class="inline-flex items-center justify-center gap-1 w-full mt-1 px-2 py-1.5 rounded-sm border border-accent/60 bg-accent/8 text-xs font-semibold text-accent transition-[border-color,background-color,color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-accent hover:bg-accent/15 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
        @click="onQuickCreateBuild"
      >{{ messages.builds.saveCurrentLoadoutAsPersonalBuild }}</button>
      <button
        v-if="props.canCreateBuild"
        type="button"
        data-build-action="create"
        class="inline-flex items-center justify-center gap-1 w-full mt-1 px-2 py-1.5 rounded-sm border border-dashed border-border-strong text-xs font-semibold text-text-secondary transition-[border-color,background-color,color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-accent hover:bg-accent/8 hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
        @click="onCreateBuild"
      >{{ messages.builds.createBuild }}</button>
      <p v-else-if="!props.canSelectBuilds && !props.canSelectGuildBuilds" class="px-2 pt-1 text-xs leading-tight text-text-muted">{{ messages.builds.liveBuildAuthorization }}</p>
      <PlayerBuildPresetRows :player="props.player" :can-apply="props.canSelectBuilds || props.canSelectGuildBuilds" @used="closePopup(true)" />
      <section
        class="mt-1 pt-1 border-t border-border"
        :aria-label="messages.builds.guildBuilds"
      >
        <header class="flex items-center justify-between gap-2 px-2 py-1">
          <h3 class="text-xs font-semibold tracking-wide uppercase text-text-muted">{{ messages.builds.guildBuilds }}</h3>
          <span class="text-2xs font-mono text-text-muted">{{ assignedGuildBuilds.length }}</span>
        </header>
        <button
          v-if="canRevealUnassignedGuildBuilds"
          type="button"
          data-build-action="toggle-unassigned-guild"
          class="inline-flex w-full items-center justify-between rounded-sm border border-dashed border-border-strong px-2 py-1.5 text-xs font-semibold text-text-secondary hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          :aria-expanded="showUnassignedGuildBuilds"
          @click="showUnassignedGuildBuilds = !showUnassignedGuildBuilds"
        >
          <span>{{ showUnassignedGuildBuilds ? messages.builds.hideAvailableBuilds : messages.builds.assignMoreBuilds }}</span>
          <span class="font-mono text-2xs text-text-muted">{{ unassignedGuildBuilds.length }}</span>
        </button>
        <div v-if="visibleGuildBuilds.length > 0" class="flex flex-col gap-1" role="group" :aria-label="messages.builds.guildBuilds">
          <template v-for="(entry, entryIndex) in visibleGuildBuilds" :key="entry.id">
            <p v-if="showUnassignedGuildBuilds && entryIndex === assignedGuildBuilds.length" class="border-t border-border px-2 pt-2 text-2xs font-semibold uppercase tracking-wide text-text-muted">{{ messages.builds.availableToAssign }}</p>
            <PlayerGuildBuildRow
              :entry="entry"
              :active="isActiveGuildBuild(entry)"
              :stage-names="stageNamesForBuild(entry.weaponSlugs)"
              :can-select="props.canSelectGuildBuilds"
              :can-use-unassigned="props.canUseUnassignedGuildBuilds"
              :can-assign="props.canAssignGuildBuilds"
              :can-manage="props.canManageGuildBuilds"
              @use="onUseGuildBuild(entry)"
              @assign="onAssignGuildBuild(entry)"
              @edit="(slot) => onEditGuildBuild(entry, slot)"
              @remove="onRemoveGuildBuild(entry)"
            />
          </template>
        </div>
        <p v-else class="px-2 py-2 text-xs leading-relaxed text-text-muted">{{ guildBuilds.length > 0 ? messages.builds.noAssignedGuildBuilds : messages.builds.noGuildBuilds }}</p>
        <button
          v-if="canQuickCreateGuildBuild"
          type="button"
          data-build-action="quick-create-guild"
          class="inline-flex items-center justify-center gap-1 w-full mt-1 px-2 py-1.5 rounded-sm border border-accent/60 bg-accent/8 text-xs font-semibold text-accent transition-[border-color,background-color,color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-accent hover:bg-accent/15 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          @click="onQuickCreateGuildBuild"
        >{{ messages.builds.saveCurrentLoadout }}</button>
        <button
          v-if="props.canCreateGuildBuild"
          type="button"
          data-build-action="create-guild"
          class="inline-flex items-center justify-center gap-1 w-full mt-1 px-2 py-1.5 rounded-sm border border-dashed border-border-strong text-xs font-semibold text-text-secondary transition-[border-color,background-color,color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-accent hover:bg-accent/8 hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          @click="onCreateGuildBuild"
        >{{ messages.builds.createGuildBuild }}</button>
      </section>
    </section>
  </AppDropdown>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { AppDropdown } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { WEAPONS_BY_SLUG } from '@/data/weapons'
import { isDistinctWeaponPair, sameWeaponPair } from '../../lib/playerWeaponBuilds'
import { buildStageSelectionsForPlayer } from '../../lib/raidStageBuilds'
import { weaponDisplayName } from '../../lib/roleLabels'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import type { GuildBuildEntry, Player } from '../../types'
import BuildStageBadges from './BuildStageBadges.vue'
import PlayerBuildPresetRows from './PlayerBuildPresetRows.vue'
import PlayerGuildBuildRow from './PlayerGuildBuildRow.vue'
import { weaponsEn } from './locales/en.ts'
import { weaponsRu } from './locales/ru.ts'

export interface PlayerBuildsPopupProps {
  readonly player: Player
  readonly open: boolean
  readonly triggerSelector: string
  readonly activeBuildIndex: number
  readonly canSelectBuilds: boolean
  readonly canCreateBuild: boolean
  readonly canSetDefaultBuild: boolean
  readonly canSelectGuildBuilds: boolean
  readonly canUseUnassignedGuildBuilds: boolean
  readonly canAssignGuildBuilds: boolean
  readonly canManageGuildBuilds: boolean
  readonly canCreateGuildBuild: boolean
}

const props = defineProps<PlayerBuildsPopupProps>()
const catalog = defineLocaleCatalog({ en: weaponsEn, ru: weaponsRu })
const messages = useLocaleMessages(catalog)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'use-build', playerId: string, buildIndex: number): void
  (e: 'add-build'): void
  (e: 'use-guild-build', playerId: string, buildId: string): void
  (e: 'add-guild-build'): void
  (e: 'edit-guild-build', buildId: string, slot: 0 | 1): void
}>()

const popupId = computed<string>(() => `builds-popup-${props.player.id}`)
const headingId = computed<string>(() => `${popupId.value}-heading`)
const dialogElement = ref<HTMLElement | null>(null)
const returnFocusTarget = ref<HTMLElement | null>(null)
const restoreFocusOnClose = ref<boolean>(false)
const showUnassignedGuildBuilds = ref<boolean>(false)
const store = useGvgPlannerStore()
const stageBuildSelections = computed(() => store.activeRaidId === null
  ? []
  : buildStageSelectionsForPlayer(
      store.raidStages,
      store.raidStageDiffs,
      props.player.id,
      props.player.mainWeapons,
    ))
const guildBuilds = computed<ReadonlyArray<GuildBuildEntry>>(() => props.player.guildBuilds)
const assignedGuildBuilds = computed<ReadonlyArray<GuildBuildEntry>>(
  () => guildBuilds.value.filter((entry) => entry.isAssigned),
)
const unassignedGuildBuilds = computed<ReadonlyArray<GuildBuildEntry>>(
  () => guildBuilds.value.filter((entry) => !entry.isAssigned),
)
const canRevealUnassignedGuildBuilds = computed<boolean>(
  () => props.canAssignGuildBuilds && unassignedGuildBuilds.value.length > 0,
)
const visibleGuildBuilds = computed<ReadonlyArray<GuildBuildEntry>>(() =>
  showUnassignedGuildBuilds.value
    ? [...assignedGuildBuilds.value, ...unassignedGuildBuilds.value]
    : assignedGuildBuilds.value,
)
const totalBuildCount = computed<number>(
  () => props.player.builds.length + visibleGuildBuilds.value.length,
)
const canQuickCreateBuild = computed<boolean>(() => {
  const currentLoadout = props.player.mainWeapons
  if (!props.canCreateBuild || !isDistinctWeaponPair(currentLoadout)) return false

  return !props.player.builds.some((build) => sameWeaponPair(build, currentLoadout))
})
const canQuickCreateGuildBuild = computed<boolean>(() => {
  const currentLoadout = props.player.mainWeapons
  if (!props.canCreateGuildBuild || !isDistinctWeaponPair(currentLoadout)) return false

  return !guildBuilds.value.some((entry) => sameWeaponPair(entry.weaponSlugs, currentLoadout))
})

function isActiveBuild(idx: number): boolean {
  return props.activeBuildIndex === idx && props.activeBuildIndex >= 0
}
function showActiveIndicator(idx: number): boolean {
  return isActiveBuild(idx) && props.player.builds.length > 1
}

function isSelectableBuild(buildIdx: number): boolean {
  const build = props.player.builds[buildIdx]
  if (build === undefined || build.length !== 2) return false

  const [firstSlug, secondSlug] = build
  return firstSlug !== undefined
    && secondSlug !== undefined
    && firstSlug.trim().length > 0
    && secondSlug.trim().length > 0
    && firstSlug !== secondSlug
}
function isSelectableGuildBuild(entry: GuildBuildEntry): boolean {
  return isDistinctWeaponPair(entry.weaponSlugs)
}
function canUseGuildBuild(entry: GuildBuildEntry): boolean {
  return isSelectableGuildBuild(entry)
    && (entry.isAssigned || props.canUseUnassignedGuildBuilds)
}

function stageNamesForBuild(weapons: ReadonlyArray<string>): ReadonlyArray<string> {
  return stageBuildSelections.value
    .filter((selection) => sameWeaponPair(selection.weaponSlugs, weapons))
    .map((selection) => selection.stageName)
}
function isActiveGuildBuild(entry: GuildBuildEntry): boolean {
  return props.player.activeGuildBuildIndex === entry.orderIndex
}
function onUseGuildBuild(entry: GuildBuildEntry): void {
  if (!props.canSelectGuildBuilds || !canUseGuildBuild(entry)) return
  emit('use-guild-build', props.player.id, entry.id)
  closePopup(true)
}

function onAssignGuildBuild(entry: GuildBuildEntry): void {
  if (!props.canAssignGuildBuilds || entry.isAssigned) return
  store.assignGuildBuild(props.player.id, entry.id)
}
function onEditGuildBuild(entry: GuildBuildEntry, slot: 0 | 1): void {
  if (!props.canManageGuildBuilds) return
  emit('edit-guild-build', entry.id, slot)
  closePopup(false)
}

function onRemoveGuildBuild(entry: GuildBuildEntry): void {
  if (!props.canManageGuildBuilds) return
  store.removeGuildBuild(entry.id)
}
function buildSlotLabels(buildIdx: number): ReadonlyArray<string | undefined> {
  const w = props.player.builds[buildIdx] ?? []
  if (w.length >= 2) return [w[0], w[1]]
  if (w.length === 1) return [w[0], undefined]
  return [undefined, undefined]
}

function weaponName(slug: string | undefined): string | undefined {
  if (!slug) return undefined
  const weapon = WEAPONS_BY_SLUG.get(slug)
  return weapon === undefined
    ? undefined
    : weaponDisplayName(weapon.slug, weapon.name, messages.value.names)
}
function weaponIcon(slug: string | undefined): string | undefined {
  if (!slug) return undefined
  return WEAPONS_BY_SLUG.get(slug)?.icon
}

function buildSummary(buildIdx: number): string {
  const names = (props.player.builds[buildIdx] ?? [])
    .map((slug) => weaponName(slug))
    .filter((name): name is string => Boolean(name))
  return names.length > 0 ? names.join(' + ') : messages.value.builds.noWeaponsSelected
}
function rowTitle(buildIdx: number): string {
  const label = buildSummary(buildIdx)
  return isActiveBuild(buildIdx)
    ? messages.value.builds.activeBuildTitle(label)
    : messages.value.builds.useBuildTitle(buildIdx + 1, label)
}

function readOnlyRowTitle(buildIdx: number): string {
  const label = buildSummary(buildIdx)
  if (!isSelectableBuild(buildIdx)) {
    return messages.value.builds.unavailableBuildTitle(buildIdx + 1, label)
  }
  return messages.value.builds.savedBuildTitle(buildIdx + 1, label)
}
function onUseBuild(buildIndex: number): void {
  if (!props.canSelectBuilds || !isSelectableBuild(buildIndex)) return
  emit('use-build', props.player.id, buildIndex)
  closePopup(true)
}

function onSetDefaultBuild(buildIndex: number): void {
  if (!props.canSetDefaultBuild || buildIndex <= 0) return
  void store.setMyDefaultBuild(buildIndex)
  closePopup(true)
}

function onCreateBuild(): void {
  emit('add-build')
  closePopup(false)
}
function onQuickCreateBuild(): void {
  if (!canQuickCreateBuild.value) return
  store.addBuild(props.player.id, props.player.mainWeapons)
  closePopup(true)
}
function onCreateGuildBuild(): void {
  if (!props.canManageGuildBuilds) return
  emit('add-guild-build')
  closePopup(false)
}

function onQuickCreateGuildBuild(): void {
  if (!canQuickCreateGuildBuild.value) return
  store.addGuildBuild(props.player.mainWeapons)
  closePopup(true)
}

function onDropdownUpdate(value: boolean): void {
  if (!value) restoreFocusOnClose.value = false
  emit('update:open', value)
}

function onDialogKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return

  event.preventDefault()
  event.stopPropagation()
  closePopup(true)
}

function closePopup(restoreFocus: boolean): void {
  restoreFocusOnClose.value = restoreFocus
  emit('update:open', false)
}

function triggerFocusTarget(): HTMLElement | null {
  const trigger = document.querySelector<HTMLElement>(props.triggerSelector)
  const activeElement = document.activeElement

  if (activeElement instanceof HTMLElement && trigger?.contains(activeElement)) {
    return activeElement
  }

  return trigger?.querySelector<HTMLButtonElement>('button:not(:disabled)') ?? null
}

function focusInitialAction(): void {
  const dialog = dialogElement.value
  if (dialog === null) return

  const activePersonal = dialog.querySelector<HTMLButtonElement>(
    '[data-build-action="select"][data-active-build="true"]',
  )
  const activeGuild = dialog.querySelector<HTMLButtonElement>(
    '[data-build-action="select-guild"][data-active-build="true"]',
  )
  const firstAction = dialog.querySelector<HTMLButtonElement>('[data-build-action]')
  const focusTarget = activePersonal ?? activeGuild ?? firstAction ?? dialog
  focusTarget.focus()
}

function restoreTriggerFocus(): void {
  const focusTarget = returnFocusTarget.value
  returnFocusTarget.value = null
  if (focusTarget?.isConnected) focusTarget.focus()
}

watch(
  () => props.open,
  (isOpen, wasOpen) => {
    if (isOpen) {
      showUnassignedGuildBuilds.value = false
      returnFocusTarget.value = triggerFocusTarget()
      void nextTick(focusInitialAction)
      return
    }

    if (wasOpen && restoreFocusOnClose.value) {
      void nextTick(restoreTriggerFocus)
    }
    restoreFocusOnClose.value = false
  },
  { flush: 'post' },
)
</script>
